/**
 * Настройки игрового режима (docs/GAME.md). Всё живёт в localStorage:
 * ни новых полей в модели данных, ни новых эндпоинтов режим не требует.
 */

import { readPref, writePref } from '../lib/prefs'

/** Порядок прохождения (docs/GAME.md, «Два порядка прохождения»). */
export type PlayOrder = 'sequential' | 'by-role'

/** Что делать с оригинальной дорожкой при сведении. */
export type MixChoice = 'mute' | 'duck'

const ORDER_KEY = 'ozvuchka.play.order'
const LISTEN_KEY = 'ozvuchka.play.listenOriginal'
const HANDOFF_KEY = 'ozvuchka.play.handoffBetweenRoles'
const AUTOPLAY_TAKE_KEY = 'ozvuchka.play.autoPlayTake'
const MIX_KEY = 'ozvuchka.play.mix'
const DUCK_KEY = 'ozvuchka.play.duckGainDb'

/** «Убрать оригинал» — полное заглушение (docs/GAME.md, «Два варианта сведения»). */
export const MUTE_ORIGINAL_DB = -99
export const DUCK_DEFAULT_DB = -12
export const DUCK_MIN_DB = -30
export const DUCK_MAX_DB = -3

export const ORDER_LABELS: Record<PlayOrder, string> = {
  sequential: 'По порядку',
  'by-role': 'По ролям',
}

export const ORDER_HINTS: Record<PlayOrder, string> = {
  sequential:
    'Реплики идут по таймкоду, как в видео. Обычный выбор, когда один человек озвучивает всех подряд.',
  'by-role':
    'Сначала все реплики первой роли, потом второй. Отыграл свою партию — передал устройство следующему.',
}

export function isPlayOrder(value: string | null): value is PlayOrder {
  return value === 'sequential' || value === 'by-role'
}

export function readOrder(): PlayOrder {
  const raw = readPref(ORDER_KEY)
  return isPlayOrder(raw) ? raw : 'sequential'
}

export function writeOrder(order: PlayOrder): void {
  writePref(ORDER_KEY, order)
}

/** По умолчанию включено: без оригинала попасть в тайминг тяжело. */
export function readListenOriginal(): boolean {
  return readPref(LISTEN_KEY) !== '0'
}

export function writeListenOriginal(enabled: boolean): void {
  writePref(LISTEN_KEY, enabled ? '1' : '0')
}

/**
 * Проигрывать ли записанный дубль сразу после записи (docs/GAME.md,
 * «Цикл записи»). По умолчанию включено: услышать результат без единого клика —
 * тот самый момент, ради которого играют. Выключают, когда пишут длинную сцену
 * или играют рядом с другими; тогда после записи сразу предлагается выбор
 * «Дальше» / «Ещё раз» / «Прослушать».
 */
export function readAutoPlayTake(): boolean {
  return readPref(AUTOPLAY_TAKE_KEY) !== '0'
}

export function writeAutoPlayTake(enabled: boolean): void {
  writePref(AUTOPLAY_TAKE_KEY, enabled ? '1' : '0')
}

/**
 * Передавать ли устройство между ролями в порядке `sequential`
 * (docs/GAME.md, «Передача устройства»). Выключено по умолчанию: этот порядок
 * выбирают, чтобы озвучить всех подряд самому, а диалог меняет роль почти
 * на каждой реплике. В `by-role` заставка есть всегда и от тумблера не зависит.
 */
export function readHandoffBetweenRoles(): boolean {
  return readPref(HANDOFF_KEY) === '1'
}

export function writeHandoffBetweenRoles(enabled: boolean): void {
  writePref(HANDOFF_KEY, enabled ? '1' : '0')
}

/** Выбор сведения запоминается между играми (docs/GAME.md). */
export function readMixChoice(): MixChoice {
  return readPref(MIX_KEY) === 'duck' ? 'duck' : 'mute'
}

export function writeMixChoice(choice: MixChoice): void {
  writePref(MIX_KEY, choice)
}

export function readDuckGainDb(): number {
  const raw = Number(readPref(DUCK_KEY))
  if (!Number.isFinite(raw)) return DUCK_DEFAULT_DB
  return Math.min(DUCK_MAX_DB, Math.max(DUCK_MIN_DB, Math.round(raw)))
}

export function writeDuckGainDb(db: number): void {
  writePref(DUCK_KEY, String(db))
}

/** Громкость оригинала для POST /api/projects/{id}/render. */
export function originalGainFor(choice: MixChoice, duckGainDb: number): number {
  return choice === 'mute' ? MUTE_ORIGINAL_DB : duckGainDb
}
